import './BattleDonate.sass';

import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, List, ListItem, LottieIcon, Switch } from '$uikit';
import { useTranslator } from '$hooks';
import { formatInputAmount } from '$utils/number';
import { toastActions } from '$store/toast';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { balanceActions, balanceSelector } from '$store/balance';
import { AmountInput, Currency } from '$shared/components';
import { BattleDonateProps } from '$core/BattleDonate/BattleDonate.interface';

const AMOUNT_LIST = [5, 10, 25, 50, 100];
const MAX_AMOUNT = 100;

const BattleDonateInner = ({ battleId, userId }: BattleDonateProps) => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const currentCurrency = CurrencyEnum.EUR;
  const minAmount = 1;

  const [amount, setAmount] = useState('5');
  const [isAnon, setAnon] = useState(false);
  const [isCustomAmountMode, setCustomAmountMode] = useState(false);

  const handlePriceChange = (value: string) => {
    setAmount(formatInputAmount(value, 2));
  };

  const handleAnon = useCallback(() => {
    setAnon(!isAnon);
  }, [isAnon]);

  const handleSend = () => {
    if (+amount > 100) {
      return dispatch(
        toastActions.setToastFail(
          t('tips_amount_description_limit_error', {
            amount: formatCurrency(100, currentCurrency),
          }),
        ),
      );
    }

    if (+amount < minAmount) {
      return dispatch(
        toastActions.setToastFail(
          t('tips_amount_description', {
            amount: formatCurrency(minAmount, currentCurrency),
          }),
        ),
      );
    }

    dispatch(
      balancePayActions.open({
        amount: +amount,
        action: BalancePayAction.BATTLE_DONATE,
        objectId: battleId,
        extraId: userId,
        isAnon,
      }),
    );
    handleClose();
  };

  const handleClose = () => {
    dispatch(balanceActions.setBattleDonateData(null));
  };

  const amountTip = useMemo(() => {
    const formattedMinAmount = formatCurrency(minAmount, currentCurrency);
    const formattedMaxAmount = formatCurrency(MAX_AMOUNT, currentCurrency);

    if (+amount < minAmount) {
      return t('tips_custom_amount_small_err', {
        amount: formattedMinAmount,
      });
    } else if (+amount > MAX_AMOUNT) {
      return t('tips_custom_amount_large_err', {
        amount: formattedMaxAmount,
      });
    }

    return t('tips_custom_amount_tip', {
      minAmount: formattedMinAmount,
      maxAmount: formattedMaxAmount,
    });
  }, [amount]);

  return (
    <BottomSheet
      onClose={handleClose}
      title={t('battle_donate_title')}
      className="Tips__BottomSheet"
    >
      <AmountInput
        amount={amount}
        onInputChange={handlePriceChange}
        title={t('tips_amount_title')}
        amountList={AMOUNT_LIST}
        isCustomAmountMode={isCustomAmountMode}
        onInlineButtonClick={() => setCustomAmountMode(true)}
        currency={currentCurrency}
        inputTip={amountTip}
      />
      <List>
        <ListItem
          description={t('battle_donate_anon_desc')}
          after={<Switch checked={isAnon} />}
          onClick={handleAnon}
        >
          {t('battle_donate_anon')}
        </ListItem>
      </List>
      <Button className="Tips__sendButton" onClick={handleSend}>
        {t('battle_donate_button')}{' '}
        <Currency amount={+amount} currency={currentCurrency} />
      </Button>
    </BottomSheet>
  );
};

const BattleDonatePaid = () => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleClose = () => {
    dispatch(balanceActions.setIsBattleDonatePaid(false));
  };

  return (
    <BottomSheet onClose={handleClose} className="Tips__BottomSheet">
      <LottieIcon name="congratulation" />
      <div className="Tips__success__title">{t('battle_donate_sent')}</div>
      <Button className="Tips__success__close" onClick={handleClose}>
        {t('tips_close')}
      </Button>
    </BottomSheet>
  );
};

export const BattleDonate = () => {
  const { battleDonateData, isBattleDonatePaid } = useSelector(balanceSelector);

  if (battleDonateData) return <BattleDonateInner {...battleDonateData} />;

  if (isBattleDonatePaid) return <BattleDonatePaid />;

  return null;
};
